import React, { useState, useEffect } from 'react';
import { XMarkIcon, ExclamationTriangleIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { feeAPI, studentAPI, batchAPI } from '../../services/api';
import { formatIndianCurrency } from '../../utils/dateUtils';
import Toast from '../Toast';

const FeeReminderForm = ({ feeStructure, onClose, onSuccess }) => {
  const [reminderAmount, setReminderAmount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [studentData, setStudentData] = useState(null);
  const [batchData, setBatchData] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Calculate outstanding amount
  const totalPaid = feeStructure.total_paid || 0;
  const outstanding = feeStructure.final_amount - totalPaid;
  const maxReminderAmount = Math.max(0, outstanding);

  // Fetch student and batch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoadingData(true);
        
        // Get student ID from fee structure
        const studentId = feeStructure.student_id || feeStructure.student?.id || feeStructure.student?.ref?.id;
        const batchId = feeStructure.batch_id || feeStructure.batch?.id || feeStructure.batch?.ref?.id;
        
        if (studentId) {
          const studentResponse = await studentAPI.getById(studentId);
          setStudentData(studentResponse.data);
        }
        
        if (batchId) {
          const batchResponse = await batchAPI.getById(batchId);
          setBatchData(batchResponse.data);
        }
      } catch (err) {
        console.error('Error fetching student/batch data:', err);
        setError('Failed to load student or batch information');
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, [feeStructure]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!reminderAmount || parseFloat(reminderAmount) <= 0) {
      setError('Please enter a valid reminder amount');
      return;
    }

    if (parseFloat(reminderAmount) > maxReminderAmount) {
      setError(`Reminder amount cannot exceed outstanding amount of ${formatIndianCurrency(maxReminderAmount)}`);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await feeAPI.sendReminder(feeStructure._id || feeStructure.id, {
        reminder_amount: parseFloat(reminderAmount)
      });

      setSuccess('Fee reminder sent successfully!');
      setTimeout(() => {
        onSuccess && onSuccess(response);
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send fee reminder');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl border border-gray-700 w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-yellow-500 rounded-lg">
              <ExclamationTriangleIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Send Fee Reminder</h3>
              <p className="text-sm text-gray-400">Remind student about outstanding fees</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-white transition-colors disabled:opacity-50"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Student Info */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-300 mb-2">Student Information</h4>
            <div className="bg-gray-700 rounded-lg p-4">
              {isLoadingData ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500"></div>
                  <span className="ml-2 text-gray-300">Loading student information...</span>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-sm">Student:</span>
                    <span className="text-white font-medium">
                      {studentData ? `${studentData.first_name} ${studentData.last_name}` : 'Unknown Student'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-sm">Batch:</span>
                    <span className="text-white font-medium">
                      {batchData ? `${batchData.batch_name} - ${batchData.subject}` : 'Unknown Batch'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Email:</span>
                    <span className="text-white font-medium text-sm">
                      {studentData?.email || 'No email available'}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Fee Summary */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-300 mb-2">Fee Summary</h4>
            <div className="bg-gray-700 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-sm">Total Fee:</span>
                <span className="text-white font-medium">
                  {formatIndianCurrency(feeStructure.final_amount)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-sm">Amount Paid:</span>
                <span className="text-green-400 font-medium">
                  {formatIndianCurrency(totalPaid)}
                </span>
              </div>
              <div className="flex items-center justify-between border-t border-gray-600 pt-3">
                <span className="text-gray-300 font-medium">Outstanding:</span>
                <span className="text-red-400 font-bold text-lg">
                  {formatIndianCurrency(outstanding)}
                </span>
              </div>
            </div>
          </div>

          {/* Reminder Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="reminderAmount" className="block text-sm font-medium text-gray-300 mb-2">
                Reminder Amount (₹)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  id="reminderAmount"
                  value={reminderAmount}
                  onChange={(e) => setReminderAmount(e.target.value)}
                  min="0"
                  max={maxReminderAmount}
                  step="0.01"
                  className="block w-full pl-10 pr-3 py-3 border border-gray-600 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  placeholder={`Max: ${formatIndianCurrency(maxReminderAmount)}`}
                  disabled={isLoading}
                />
              </div>
              <p className="mt-1 text-xs text-gray-400">
                Maximum reminder amount: {formatIndianCurrency(maxReminderAmount)}
              </p>
            </div>

            {/* Error/Success Messages */}
            {error && (
              <div className="bg-red-900/30 border border-red-700 rounded-lg p-3">
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            )}

            {success && (
              <div className="bg-green-900/30 border border-green-700 rounded-lg p-3">
                <p className="text-green-300 text-sm">{success}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={isLoading}
                className="flex-1 px-4 py-3 border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading || isLoadingData || !reminderAmount || parseFloat(reminderAmount) <= 0 || parseFloat(reminderAmount) > maxReminderAmount || !studentData}
                className="flex-1 px-4 py-3 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 focus:ring-offset-gray-800 transition-colors"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span className="ml-2">Sending...</span>
                  </div>
                ) : (
                  'Send Reminder'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Toast for success */}
      {success && <Toast message={success} type="success" />}
    </div>
  );
};

export default FeeReminderForm; 